import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import { useGroupCommand } from "src/providers/GroupCommandProvider";
import { RootState } from "src/store";

import {
    NewCommandDialog,
    useNewCommandDialogChunk,
} from "@dashboard/devices/components/Commands/NewCommandDialog/NewCommandDialog.lazy";
import { selectDevicesByGroupId } from "@dashboard/devices/store/selectors/devices";
import { Group } from "@dashboard/devices/types";
import { MdmCommand } from "@devices/types/commands";
import { SecondaryButton } from "@shared/CustomButton";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { PaneElement } from "../../shared/PaneElement";

import GroupCommandItem from "./GroupCommandItem";

type CommandsProps = {
    group: Group;
};

const columnHelper = createColumnHelper<MdmCommand>();

const columns = [
    columnHelper.accessor("id", {
        id: "collapse",
        header: "",
        cell: () => null,
        enableSorting: false,
    }),
    columnHelper.accessor("created", {
        id: "date",
        header: i18next.t("common:label.date"),
        cell: (info) => info.getValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("status", {
        id: "status",
        header: i18next.t("common:label.status"),
        cell: (info) => info.getValue(),
        meta: { align: "center" },
        enableSorting: false,
    }),
    columnHelper.accessor("payload", {
        id: "command",
        header: i18next.t("sidepanels:groupPanel.table.header.command"),
        cell: (info) => info.getValue(),
        enableSorting: true,
    }),
    columnHelper.accessor("id", {
        id: "actions",
        header: "",
        cell: () => null,
        enableSorting: false,
    }),
];

const CommandsTab = ({ group }: CommandsProps) => {
    const { t } = useTranslation(["sidepanels", "common", "modals"]);
    const groupDevices = useSelector((state: RootState) => selectDevicesByGroupId(state, group.id));
    const [dialogOpen, setDialogOpen] = useState(false);
    const { isLoading: isDialogLoading, load: loadDialog } = useNewCommandDialogChunk();
    const commandsContext = useGroupCommand();

    const table = useReactTable({
        data: commandsContext.data,
        columns,
        state: {
            sorting: commandsContext.sorting,
            pagination: {
                pageIndex: commandsContext.pageIndex,
                pageSize: commandsContext.pageSize,
            },
        },
        onSortingChange: commandsContext.setSorting,
        pageCount: Math.ceil(commandsContext.totalCount / commandsContext.pageSize),
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const openDialog = async () => {
        if (await loadDialog()) {
            setDialogOpen(true);
        }
    };

    const handlePageChange = (_: unknown, newPage: number) => {
        commandsContext.setPageIndex(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        commandsContext.setPageSize(newPageSize);
        commandsContext.setPageIndex(0);
    };

    const getTooltipTitle = () => {
        if (isDialogLoading) {
            return t("common:tooltip.featureLoading");
        }

        if (!groupDevices.length) {
            return t("sidepanels:groupPanel.tooltip.noDevices");
        }

        return t("sidepanels:groupPanel.tooltip.createCommand");
    };

    return (
        <PaneElement.Container>
            <PaneElement.TitleRow title={t("sidepanels:groupPanel.commandsTab.title")}>
                <SecondaryButton
                    data-testid="create-command-button"
                    onClick={openDialog}
                    disabled={!groupDevices.length || isDialogLoading}
                    loading={isDialogLoading}
                    tooltipProps={{ title: getTooltipTitle() }}
                >
                    <i className="fa-solid fa-plus" />
                </SecondaryButton>
            </PaneElement.TitleRow>

            <PaneElement.TableLayout
                contentDeps={[commandsContext.pageIndex, commandsContext.pageSize, commandsContext.data.length]}
                pagination={
                    <CustomTablePagination
                        component="div"
                        count={commandsContext.totalCount}
                        rowsPerPageOptions={commandsContext.rowsPerPage}
                        rowsPerPage={commandsContext.pageSize}
                        page={commandsContext.pageIndex}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton
                        showLastButton
                    />
                }
            >
                <MinimalTable>
                    <MinimalTableHeader>
                        <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        loading={commandsContext.isLoading}
                        noDataMessage={t("sidepanels:groupPanel.table.noCommands")}
                    >
                        {table.getRowModel().rows.map((row, index) => (
                            <GroupCommandItem
                                key={row.original.fingerprint}
                                command={row.original}
                                index={index}
                                deleteCommand={commandsContext.onDelete}
                                createCommand={commandsContext.onCreate}
                                isCreating={commandsContext.resendingId === row.original.id}
                                isDeleting={commandsContext.deletingId === row.original.id}
                            />
                        ))}
                    </TableBody>
                </MinimalTable>
            </PaneElement.TableLayout>
            {dialogOpen ? (
                <NewCommandDialog
                    title={t("modals:newCommand.title")}
                    devices={groupDevices}
                    toggleDialog={() => setDialogOpen(false)}
                    createCommand={commandsContext.onCreate}
                />
            ) : null}
        </PaneElement.Container>
    );
};

export default CommandsTab;
